import {
  type SpeakerLiveTranscriptTurn,
  groupWordsBySpeaker,
  isReconnectDivider,
} from "@components/AssemblyAITranscriber/segments";
import {
  type LiveTranscriptTurn,
  formatHhmmss,
} from "@components/Transcriber/segments";

import type { LiveTranscriptionSegment } from "./LiveTranscriptionFeed";
import { formatDuration } from "./ScribeSession";

// Row keys derive from the block's first turn so a SpeakerRevision updates
// rows in place; dividers always end a block since labels restart per connection.
export function toLiveFeedSegments(
  turns: LiveTranscriptTurn[] | null
): LiveTranscriptionSegment[] {
  if (!turns) return [];
  const timestampOf = (turn: LiveTranscriptTurn) =>
    turn.timestamp_hhmmss?.[0] ??
    (turn.timestamp?.[0] !== undefined
      ? formatDuration(turn.timestamp[0] * 1000)
      : "");

  const rows = turns.flatMap((turn): LiveTranscriptionSegment[] => {
    if (isReconnectDivider(turn)) {
      return [
        { text: "", timestamp: timestampOf(turn), key: turn.key, divider: true },
      ];
    }
    const key = (turn as Partial<SpeakerLiveTranscriptTurn>).key;
    if (!key) {
      return [{ text: turn.text || "", timestamp: timestampOf(turn) }];
    }
    return groupWordsBySpeaker(turn as SpeakerLiveTranscriptTurn).map((run, i) => ({
      text: run.text,
      timestamp: i === 0 ? timestampOf(turn) : formatHhmmss(run.startSec),
      key: `${key}#${i}`,
      ...(run.speaker ? { speaker: run.speaker } : {}),
    }));
  });

  return mergeSameSpeakerRows(rows);
}

function mergeSameSpeakerRows(
  rows: LiveTranscriptionSegment[]
): LiveTranscriptionSegment[] {
  const merged: LiveTranscriptionSegment[] = [];
  for (const row of rows) {
    const prev = merged[merged.length - 1];
    const bothSpeakerRows =
      prev && prev.key && !prev.divider && row.key && !row.divider;
    // Unlabelled speech joins a labelled block, but never starts one.
    if (
      bothSpeakerRows &&
      prev.speaker &&
      (row.speaker === prev.speaker || !row.speaker)
    ) {
      merged[merged.length - 1] = { ...prev, text: `${prev.text} ${row.text}` };
      continue;
    }
    merged.push(row);
  }
  return merged;
}
